import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import {
  getProductionTestCrmFixedPerson,
  getProductionTestCrmStatus,
  isProductionTestCrmOrg,
  PRODUCTION_TEST_CRMS,
  validProductionTestCrmFixedPerson,
  validProductionTestCrmStatus,
  type ProductionTestCrmFixedPerson,
  type ProductionTestCrmStatus,
} from '../../../api/productionTestCrmReads';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedOrgId?: string;
  currentRowId?: string;
  freshDetailId?: string;
  freshDetailActive?: boolean;
  freshDetailLoading?: boolean;
}

const CINC_ORG_ID = '70299cd6-a817-4eeb-a333-df660b1f204e';
const CAPABILITIES = ['people', 'users', 'notes', 'tasks', 'transactions'];

export const ProductionTestCrmReadModal: React.FC<Props> = ({
  isOpen, onClose, selectedOrgId, currentRowId, freshDetailId, freshDetailActive,
  freshDetailLoading,
}) => {
  const { user, isSuperAdmin } = useUser();
  const { user: authUser, loading: authLoading } = useAuthStore();
  // GraphQL may return the same cached user and role after Firebase replaces
  // its session for that UID. Track the auth object transition itself.
  const authEpochRef = useRef({ user: authUser, epoch: 0 });
  if (authEpochRef.current.user !== authUser) {
    authEpochRef.current = { user: authUser, epoch: authEpochRef.current.epoch + 1 };
  }
  const orgId = isProductionTestCrmOrg(selectedOrgId) ? selectedOrgId : undefined;
  const eligible = isOpen && import.meta.env.VITE_APP_ENV === 'production' &&
    isSuperAdmin && Boolean(user?.id) && Boolean(authUser?.uid) && !authLoading && Boolean(orgId) &&
    currentRowId === orgId && freshDetailId === orgId && freshDetailActive === false &&
    freshDetailLoading === false;

  // Change generation during render so an A -> B -> A or close/reopen transition
  // cannot reuse a request started in an earlier context.
  const identity = [isOpen, selectedOrgId, currentRowId, freshDetailId,
    freshDetailActive, freshDetailLoading, isSuperAdmin, user?.id,
    authUser?.uid, authLoading, authEpochRef.current.epoch, orgId].join('|');
  const contextRef = useRef({ identity, generation: 0 });
  if (contextRef.current.identity !== identity) {
    contextRef.current = { identity, generation: contextRef.current.generation + 1 };
  }
  const generation = contextRef.current.generation;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [status, setStatus] = useState<{
    generation: number;
    phase: 'loading' | 'ready' | 'error';
    data?: ProductionTestCrmStatus;
  } | null>(null);
  const [person, setPerson] = useState<{
    generation: number;
    phase: 'loading' | 'ready' | 'error';
    data?: ProductionTestCrmFixedPerson;
  } | null>(null);
  const inFlightRef = useRef<number | null>(null);
  const currentStatus = status?.generation === generation ? status : null;
  const currentPerson = person?.generation === generation ? person : null;
  const isCurrent = (started: number) => mountedRef.current &&
    started === contextRef.current.generation;

  const inspectStatus = async () => {
    if (!eligible || !orgId || inFlightRef.current === generation) return;
    const started = generation;
    inFlightRef.current = started;
    setStatus({ generation: started, phase: 'loading' });
    setPerson(null);
    try {
      const data = await getProductionTestCrmStatus(orgId);
      if (isCurrent(started)) setStatus(validProductionTestCrmStatus(orgId, data)
        ? { generation: started, phase: 'ready', data }
        : { generation: started, phase: 'error' });
    } catch {
      if (isCurrent(started)) setStatus({ generation: started, phase: 'error' });
    } finally {
      if (inFlightRef.current === started) inFlightRef.current = null;
    }
  };

  const inspectFixedPerson = async () => {
    if (!eligible || !orgId || orgId === CINC_ORG_ID ||
        currentStatus?.phase !== 'ready' || inFlightRef.current === generation) return;
    const started = generation;
    inFlightRef.current = started;
    setPerson({ generation: started, phase: 'loading' });
    try {
      const data = await getProductionTestCrmFixedPerson(orgId);
      if (isCurrent(started)) setPerson(validProductionTestCrmFixedPerson(data)
        ? { generation: started, phase: 'ready', data }
        : { generation: started, phase: 'error' });
    } catch {
      if (isCurrent(started)) setPerson({ generation: started, phase: 'error' });
    } finally {
      if (inFlightRef.current === started) inFlightRef.current = null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="space-y-4 p-5">
        <Typography variant="h3">Test CRM read-only inspection</Typography>
        <p>Connection status and one fixed synthetic person can be inspected manually. Import, sync and outreach remain off.</p>
        {orgId && <p>{PRODUCTION_TEST_CRMS[orgId].label}</p>}
        {!eligible && <p>Inspection unavailable for this selection.</p>}
        <Button type="button" disabled={!eligible || currentStatus?.phase === 'loading' ||
          currentPerson?.phase === 'loading'}
          onClick={inspectStatus}>Read connection status</Button>
        {currentStatus?.phase === 'loading' && <p>Reading connection status...</p>}
        {currentStatus?.phase === 'error' && <p>Connection status unavailable or not qualified.</p>}
        {currentStatus?.phase === 'ready' && currentStatus.data && (
          <div>
            <p>{currentStatus.data.status === 'existing_active_routing_fenced'
              ? 'Existing CINC binding; production routing fenced.'
              : 'Credential bound; sync, import and outreach disabled.'}</p>
            <p>Provider account not qualified for import or outreach.</p>
            <p>Plan: {currentStatus.data.plan ?? 'unavailable'}; authentication: {currentStatus.data.auth_type ?? 'unavailable'}.</p>
            <p>Read capabilities: {currentStatus.data.metadata.read_capabilities
              .filter((capability) => CAPABILITIES.includes(capability)).join(', ') || 'none reported'}.</p>
            {orgId !== CINC_ORG_ID && (
              <Button type="button" disabled={!eligible ||
                currentPerson?.phase === 'loading'}
                onClick={inspectFixedPerson}>Read fixed synthetic person</Button>
            )}
          </div>
        )}
        {currentPerson?.phase === 'loading' && <p>Reading fixed synthetic person...</p>}
        {currentPerson?.phase === 'error' && <p>Fixed synthetic person unavailable or not qualified.</p>}
        {currentPerson?.phase === 'ready' && <p>Fixed synthetic person matched; import and outreach remain disabled.</p>}
      </div>
    </Modal>
  );
};
